export interface ScanBufferOptions {
  /**
   * Largest gap, in milliseconds, allowed between two consecutive
   * keystrokes (including the terminator) before the run stops looking
   * like a scan. Keyboard-wedge scanners typically emit every character
   * within ~10-20ms; human typing rarely sustains under ~60-80ms across a
   * whole code. 30ms is a starting point, not a measured value - there is
   * no physical scanner in the environment this was written in
   * (docs/android/PHASE-6-BASELINE-AUDIT.md SS2), so treat it as
   * provisional until it can be tuned against real hardware.
   */
  maxInterKeyMs?: number;
  /** Shortest string length that is still treated as a possible scan. */
  minChars?: number;
  /** Longest buffer kept before giving up on the run as pathological. */
  maxChars?: number;
}

const DEFAULTS: Required<ScanBufferOptions> = {
  maxInterKeyMs: 30,
  minChars: 3,
  maxChars: 64,
};

/**
 * Framework-free keyboard-wedge scan detector (Phase 6 brief SS21: a
 * barcode scanner types like a keyboard, terminated by Enter/Tab, and is
 * indistinguishable from a person except by inter-keystroke timing).
 *
 * Feed it every plain-character keystroke via `push`, and the terminator
 * key via `finish`. `finish` returns the accumulated code only if every
 * gap between characters - and the gap before the terminator itself - was
 * fast enough to be a scan; otherwise it returns null and the buffer is
 * dropped, so normal human typing that happens to end in Enter is never
 * mistaken for a scan.
 *
 * Deliberately conservative: a single slow gap invalidates the whole
 * buffer rather than just being ignored. A person can hit two nearby keys
 * close together by chance, but cannot sustain scanner-grade speed across
 * an entire code, so this favours missing an occasional real scan (the
 * cashier just re-scans) over ever auto-adding an item from ordinary
 * typing.
 */
export class ScanBuffer {
  private readonly opts: Required<ScanBufferOptions>;
  private chars: string[] = [];
  private lastAt: number | null = null;
  private looksLikeScan = true;

  constructor(options: ScanBufferOptions = {}) {
    this.opts = { ...DEFAULTS, ...options };
  }

  get isEmpty(): boolean {
    return this.chars.length === 0;
  }

  /** Feed one printable character with its event timestamp (ms). */
  push(char: string, at: number): void {
    if (this.lastAt !== null && at - this.lastAt > this.opts.maxInterKeyMs) {
      this.reset();
    }
    this.chars.push(char);
    this.lastAt = at;
    if (this.chars.length > this.opts.maxChars) {
      this.looksLikeScan = false;
    }
  }

  /**
   * Feed the terminator keystroke (Enter/Tab). Returns the scanned code if
   * the whole run - including the gap before this terminator - looked
   * scanner-fast, otherwise null. Always clears the buffer.
   */
  finish(at: number): string | null {
    const code = this.chars.join('');
    const terminatorGapOk = this.lastAt === null || at - this.lastAt <= this.opts.maxInterKeyMs;
    const result =
      this.looksLikeScan && terminatorGapOk && code.length >= this.opts.minChars ? code : null;
    this.reset();
    return result;
  }

  /** Drop whatever has been buffered without treating it as a scan. */
  reset(): void {
    this.chars = [];
    this.lastAt = null;
    this.looksLikeScan = true;
  }
}
